import "./styles.css";

import {
  MAX_LINK_DISTANCE,
  MAX_LINKS_PER_BODY,
  LINK_TENSION_BREAK_FORCE,
  MIN_BODY_DISTANCE,
  WORLD_BOUNDS,
  type GooBody,
  type GooLink,
  type PlacementFailure,
  type ServerMessage,
  type TowerStats,
} from "../shared/protocol";
import { COPY, LANGUAGE_KEY, type Language } from "./i18n";
import { drawScenery } from "./scenery";

type RenderBody = GooBody & { drawX: number; drawY: number; drawAngle: number };
type Point = { x: number; y: number };
type PlacementResult = { ok: true; bodyId: number } | { ok: false; reason: PlacementFailure };

type ModelContext = {
  registerTool(tool: {
    name: string;
    title: string;
    description: string;
    inputSchema: object;
    annotations: { readOnlyHint: boolean; untrustedContentHint: boolean };
    execute(input: unknown): unknown | Promise<unknown>;
  }, options?: { signal?: AbortSignal }): void | Promise<void>;
};

let language = readLanguage();
const initialCopy = COPY[language];

const app = document.querySelector<HTMLDivElement>("#app")!;
app.innerHTML = `
  <main class="game-shell">
    <header class="topbar">
      <a id="brand" class="brand" href="https://games.ddsq.si/" aria-label="${initialCopy.back}">
        <span class="brand-mark">DD/SQ</span>
        <span class="brand-title"><strong id="brand-title">${initialCopy.title}</strong><small id="brand-subtitle">${initialCopy.subtitle}</small></span>
      </a>
      <div class="header-actions">
        <button id="help" class="quiet-button" type="button">${initialCopy.help}</button>
        <fieldset id="language-switch" class="language-switch" aria-label="${initialCopy.languageLabel}">
          <button type="button" data-language="sl" aria-pressed="${language === "sl"}">SL</button>
          <button type="button" data-language="en" aria-pressed="${language === "en"}">EN</button>
        </fieldset>
        <div id="connection" class="server-pill connecting"><span></span><b>${initialCopy.connection.connecting}</b></div>
      </div>
    </header>

    <section id="stage" class="stage" aria-label="${initialCopy.stageAria}">
      <canvas id="tower" tabindex="0" aria-label="${initialCopy.canvasAria}"></canvas>

      <div class="map-card"><span id="round-label">${initialCopy.round} 1</span><strong id="level-title">${initialCopy.towerTitle}</strong><small id="goal-copy">${initialCopy.goal}</small></div>
      <div id="height-mark" class="height-mark">0,0 m</div>
      <aside id="stats" class="stats" aria-label="${initialCopy.statsAria}">
        <div><span id="goo-count-label">${initialCopy.gooCount}</span><strong><b id="body-count">—</b> <small>/ 2000</small></strong></div>
        <div><span id="builders-label">${initialCopy.builders}</span><strong id="player-count">—</strong></div>
        <div class="desktop-stat"><span id="moving-label">${initialCopy.moving}</span><strong id="active-count">—</strong></div>
      </aside>

      <div id="hover-card" class="hover-card" hidden></div>
      <div id="tip" class="tip">${initialCopy.towerTip}</div>
      <div id="stress-legend" class="stress-legend" hidden>
        <span id="tension-label">${initialCopy.tension}</span><span class="stress-ramp" aria-hidden="true"></span><span id="compression-label">${initialCopy.compression}</span>
      </div>
      <div id="toast" class="toast" role="status" aria-live="polite" hidden></div>

      <nav id="tools" class="tools" aria-label="${initialCopy.toolsAria}">
        <button class="tool active" data-mode="build" type="button"><span>+</span> <b id="build-label">${initialCopy.build}</b></button>
        <button class="tool" data-mode="pan" type="button"><span>↔</span> <b id="pan-label">${initialCopy.pan}</b></button>
        <button id="stress-toggle" class="tool" type="button" aria-pressed="false"><span>◉</span> <b id="stress-label">${initialCopy.stress}</b></button>
        <i></i>
        <button id="zoom-out" class="icon-tool" type="button" aria-label="${initialCopy.zoomOut}">−</button>
        <button id="zoom-in" class="icon-tool" type="button" aria-label="${initialCopy.zoomIn}">+</button>
        <button id="reset-vote" class="reset-tool" type="button" aria-label="${initialCopy.resetVoteAria}" aria-pressed="false">↻ ${initialCopy.reset} 0/1</button>
        <button id="overview" class="top-tool overview-tool" type="button">⊞ ${initialCopy.overview}</button>
        <button id="focus-top" class="top-tool" type="button">${initialCopy.focusTower}</button>
      </nav>

      <div id="loading" class="loading-card"><span class="goo-loader"></span><strong id="loading-title">${initialCopy.loadingTitle}</strong><small id="loading-body">${initialCopy.loadingBody}</small></div>
      <div id="victory" class="victory-card" hidden>
        <span id="victory-eyebrow">${initialCopy.victoryEyebrow}</span>
        <strong id="victory-title">${initialCopy.victoryReached}</strong>
        <small><span id="next-map-label">${initialCopy.nextMap}</span> <b id="victory-countdown">8</b> <span id="seconds-label">${initialCopy.seconds}</span></small>
      </div>
    </section>

    <dialog id="help-dialog">
      <button id="close-help" class="dialog-close" type="button" aria-label="${initialCopy.close}">×</button>
      <p id="help-eyebrow" class="eyebrow">${initialCopy.help}</p>
      <h2 id="help-title">${initialCopy.helpTitle}</h2>
      <ol>
        <li><b id="help-build-label">${initialCopy.build}:</b> <span id="help-build-copy">${initialCopy.helpBuild}</span></li>
        <li><b id="help-pan-label">${initialCopy.pan}:</b> <span id="help-pan-copy">${initialCopy.helpPan}</span></li>
        <li><b id="help-stress-label">${initialCopy.stress}:</b> <span id="help-stress-copy">${initialCopy.helpStress}</span></li>
        <li id="help-server-copy">${initialCopy.helpServer}</li>
      </ol>
      <p id="help-note" class="dialog-note">${initialCopy.helpNote}</p>
    </dialog>
  </main>
`;

const canvas = document.querySelector<HTMLCanvasElement>("#tower")!;
const context = canvas.getContext("2d", { alpha: false })!;
const connectionElement = document.querySelector<HTMLDivElement>("#connection")!;
const bodyCountElement = document.querySelector<HTMLElement>("#body-count")!;
const playerCountElement = document.querySelector<HTMLElement>("#player-count")!;
const activeCountElement = document.querySelector<HTMLElement>("#active-count")!;
const heightElement = document.querySelector<HTMLElement>("#height-mark")!;
const roundElement = document.querySelector<HTMLElement>("#round-label")!;
const levelTitleElement = document.querySelector<HTMLElement>("#level-title")!;
const goalCopyElement = document.querySelector<HTMLElement>("#goal-copy")!;
const focusTopButton = document.querySelector<HTMLButtonElement>("#focus-top")!;
const tipElement = document.querySelector<HTMLElement>("#tip")!;
const stressLegend = document.querySelector<HTMLDivElement>("#stress-legend")!;
const stressButton = document.querySelector<HTMLButtonElement>("#stress-toggle")!;
const hoverCard = document.querySelector<HTMLDivElement>("#hover-card")!;
const toast = document.querySelector<HTMLDivElement>("#toast")!;
const loading = document.querySelector<HTMLDivElement>("#loading")!;
const victory = document.querySelector<HTMLDivElement>("#victory")!;
const victoryTitle = document.querySelector<HTMLElement>("#victory-title")!;
const victoryCountdown = document.querySelector<HTMLElement>("#victory-countdown")!;
const helpDialog = document.querySelector<HTMLDialogElement>("#help-dialog")!;
const resetVoteButton = document.querySelector<HTMLButtonElement>("#reset-vote")!;

const bodies = new Map<number, RenderBody>();
const links = new Map<number, GooLink>();
const linkStress = new Map<number, number>();
const placementPromises = new Map<string, { resolve: (value: PlacementResult) => void; timeout: number }>();
const arrivals = new Map<number, { from: Point; to: Point; startedAt: number }>();
let stats: TowerStats = {
  bodyCount: 0, maxBodies: 2_000, linkCount: 0, activeBodies: 0, players: 0, tickMs: 0, height: 0, savedAt: null,
  progression: { difficulty: 1, wins: 0, startedAt: Date.now(), lastWinAt: null, lastDecayAt: Date.now() },
  level: {
    type: "tower", round: 1, title: "Stolp do neba", goalValue: 200,
    grounds: [{ x: -3.25, y: 0.8, width: 6.5, height: 1.6 }, { x: 3.25, y: 0.8, width: 6.5, height: 1.6 }],
    goalLine: { axis: "y", value: -200 }, source: { x: -4.7, y: -1.2 }, completed: false, completedBy: null, nextLevelAt: null,
  },
};
let socket: WebSocket | null = null;
let reconnectTimer = 0;
let reconnectDelay = 800;
let connectionState: "connecting" | "online" | "offline" = "connecting";
let resetVote = { votes: 0, required: 1, players: 1, voted: false };
let mode: "build" | "pan" = "build";
let stressEnabled = false;
let pointerWorld: Point | null = null;
let pointerScreen: Point | null = null;
let pointerDown: Point | null = null;
let panning = false;
let pendingPlacement = false;
let toastTimer = 0;
let firstSnapshot = true;
let autoFollow = true;
let viewportWidth = 0;
let viewportHeight = 0;
let scale = 56;
const camera = { x: 0, y: -2.5 };
const edgePan = { x: 0, y: 0 };
const EDGE_PAN_MARGIN = 64;
const EDGE_PAN_SPEED = 320;
let lastFrameAt = performance.now();

const nickname = loadNickname();
applyLanguage(language);
connect();
registerWebMcp();

function connect() {
  window.clearTimeout(reconnectTimer);
  setConnection("connecting");
  const socketUrl = new URL(`${import.meta.env.BASE_URL}ws`, window.location.origin);
  socketUrl.protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  socket = new WebSocket(socketUrl);

  socket.addEventListener("open", () => {
    reconnectDelay = 800;
    setConnection("online");
    socket?.send(JSON.stringify({ type: "hello", name: nickname }));
    if (stressEnabled) socket?.send(JSON.stringify({ type: "set_stress", enabled: true }));
  });
  socket.addEventListener("message", (event) => {
    try {
      handleServerMessage(JSON.parse(String(event.data)) as ServerMessage);
    } catch (error) {
      console.warn(COPY[language].invalidServerMessage, error);
    }
  });
  socket.addEventListener("close", () => {
    setConnection("offline");
    reconnectTimer = window.setTimeout(connect, reconnectDelay);
    reconnectDelay = Math.min(reconnectDelay * 1.7, 10_000);
  });
  socket.addEventListener("error", () => socket?.close());
}

function handleServerMessage(message: ServerMessage) {
  if (message.type === "snapshot" || message.type === "level_updated") {
    bodies.clear();
    links.clear();
    linkStress.clear();
    for (const body of message.bodies) bodies.set(body.id, { ...body, drawX: body.x, drawY: body.y, drawAngle: body.angle });
    for (const link of message.links) links.set(link.id, link);
    updateStats(message.stats);
    loading.hidden = true;
    victory.hidden = !message.stats.level.completed;
    if (message.stats.level.completed) updateVictory(message.stats.level.completedBy);
    if (firstSnapshot) fitTower();
    firstSnapshot = false;
    if (message.type === "level_updated") showToast(COPY[language].eased);
    return;
  }
  if (message.type === "delta") {
    for (const [id, x, y, angle] of message.bodies) {
      const body = bodies.get(id);
      if (body) Object.assign(body, { x, y, angle });
    }
    if (message.stats) updateStats(message.stats);
    return;
  }
  if (message.type === "stress") {
    if (!stressEnabled) return;
    for (const [id, force] of message.values) linkStress.set(id, force);
    return;
  }
  if (message.type === "added") {
    bodies.set(message.body.id, { ...message.body, drawX: message.body.x, drawY: message.body.y, drawAngle: message.body.angle });
    for (const link of message.links) links.set(link.id, link);
    updateStats(message.stats);
    if (autoFollow) {
      if (stats.level.type === "tower" && message.body.y < camera.y - viewportHeight * 0.28 / scale) focusTop();
      if (stats.level.type !== "tower" && (Math.abs(message.body.x - camera.x) > viewportWidth * 0.32 / scale ||
          message.body.y > camera.y + viewportHeight * 0.16 / scale || message.body.y < camera.y - viewportHeight * 0.34 / scale)) focusTop();
    }
    return;
  }
  if (message.type === "removed") {
    for (const id of message.bodyIds) bodies.delete(id);
    for (const id of message.linkIds) links.delete(id);
    for (const id of message.linkIds) linkStress.delete(id);
    updateStats(message.stats);
    return;
  }
  if (message.type === "placement_result") {
    const pending = placementPromises.get(message.requestId);
    if (!pending) return;
    window.clearTimeout(pending.timeout);
    placementPromises.delete(message.requestId);
    pending.resolve(message.ok ? { ok: true, bodyId: message.bodyId } : { ok: false, reason: message.reason });
    return;
  }
  if (message.type === "level_completed") {
    updateStats(message.stats);
    updateVictory(message.level.completedBy);
    victory.hidden = false;
    return;
  }
  if (message.type === "level_started") {
    bodies.clear();
    links.clear();
    linkStress.clear();
    for (const body of message.bodies) bodies.set(body.id, { ...body, drawX: body.x, drawY: body.y, drawAngle: body.angle });
    for (const link of message.links) links.set(link.id, link);
    updateStats(message.stats);
    victory.hidden = true;
    fitTower();
    showToast(message.stats.level.type === "tower" ? COPY[language].newTower : `${levelTitleElement.textContent} · ${COPY[language].connectAll}`);
    return;
  }
  if (message.type === "reset_vote") {
    resetVote = message;
    updateResetVoteButton();
    return;
  }
  if (message.type === "notice") showToast(localizeNotice(message.message), message.level === "warning");
}

function requestPlacement(x: number, y: number): Promise<PlacementResult> {
  if (!socket || socket.readyState !== WebSocket.OPEN) return Promise.resolve({ ok: false, reason: "server_starting" });
  const requestId = crypto.randomUUID();
  socket.send(JSON.stringify({ type: "place", requestId, x, y }));
  return new Promise((resolve) => {
    const timeout = window.setTimeout(() => {
      placementPromises.delete(requestId);
      resolve({ ok: false, reason: "server_starting" });
    }, 5_000);
    placementPromises.set(requestId, { resolve, timeout });
  });
}

async function placeAt(point: Point) {
  if (pendingPlacement) return;
  const candidate = inspectCandidate(point);
  if (!candidate.valid) {
    showToast(candidate.reason, true);
    return;
  }
  pendingPlacement = true;
  canvas.classList.add("placing");
  const result = await requestPlacement(round(point.x, 3), round(point.y, 3));
  pendingPlacement = false;
  canvas.classList.remove("placing");
  if (result.ok) {
    arrivals.set(result.bodyId, { from: { ...stats.level.source }, to: { ...point }, startedAt: performance.now() });
  }
  else showToast(failureMessage(result.reason), true);
}

function inspectCandidate(point: Point) {
  if (stats.bodyCount >= stats.maxBodies) return { valid: false, neighbors: [] as RenderBody[], reason: failureMessage("world_full") };
  if (point.x < WORLD_BOUNDS.minX || point.x > WORLD_BOUNDS.maxX || point.y < WORLD_BOUNDS.minY || point.y > WORLD_BOUNDS.maxY) {
    return { valid: false, neighbors: [] as RenderBody[], reason: failureMessage("invalid_position") };
  }
  if (stats.level.grounds.some((ground) => Math.abs(point.x - ground.x) < ground.width / 2 + 0.22 && Math.abs(point.y - ground.y) < ground.height / 2 + 0.22)) {
    return { valid: false, neighbors: [] as RenderBody[], reason: COPY[language].groundError };
  }
  const nearest = [...bodies.values()]
    .map((body) => ({ body, distance: Math.hypot(point.x - body.x, point.y - body.y) }))
    .sort((a, b) => a.distance - b.distance);
  if (nearest[0]?.distance < MIN_BODY_DISTANCE) return { valid: false, neighbors: [] as RenderBody[], reason: failureMessage("too_close") };
  const nearby = nearest.filter((item) => item.distance <= MAX_LINK_DISTANCE);
  const origin = connectedBodies(stats.level.type === "tower" ? "base" : "start");
  const required = nearby.find((item) => origin.has(item.body.id));
  if (!required) return { valid: false, neighbors: [] as RenderBody[], reason: failureMessage("too_far") };
  const neighbors = [
    required.body,
    ...nearby.filter((item) => item.body.id !== required.body.id).map((item) => item.body),
  ].slice(0, MAX_LINKS_PER_BODY);
  if (neighbors.length === 0) return { valid: false, neighbors, reason: failureMessage("too_far") };
  return { valid: true, neighbors, reason: "" };
}

function connectedBodies(group: "base" | "start" | "goal"): Set<number> {
  const connected = new Set([...bodies.values()].filter((body) => body.anchorGroup === group).map((body) => body.id));
  const neighbors = new Map<number, number[]>();
  for (const link of links.values()) {
    if (!neighbors.has(link.a)) neighbors.set(link.a, []);
    if (!neighbors.has(link.b)) neighbors.set(link.b, []);
    neighbors.get(link.a)!.push(link.b);
    neighbors.get(link.b)!.push(link.a);
  }
  const queue = [...connected];
  for (let index = 0; index < queue.length; index += 1) {
    for (const id of neighbors.get(queue[index]) ?? []) {
      if (!connected.has(id)) { connected.add(id); queue.push(id); }
    }
  }
  return connected;
}

function updateStats(next: TowerStats) {
  stats = next;
  const copy = COPY[language];
  bodyCountElement.textContent = new Intl.NumberFormat(language === "sl" ? "sl-SI" : "en-US").format(next.bodyCount);
  playerCountElement.textContent = String(next.players);
  activeCountElement.textContent = String(next.activeBodies);
  roundElement.textContent = `${copy.round} ${next.level.round} · ${copy.difficulty} ${next.progression.difficulty}${next.level.relief ? ` · ${copy.assistance}` : ""}`;
  levelTitleElement.textContent = { tower: copy.towerTitle, bridge: copy.bridgeTitle, archipelago: copy.archipelagoTitle, hanging: copy.hangingTitle }[next.level.type];
  tipElement.textContent = { tower: copy.towerTip, bridge: copy.bridgeTip, archipelago: copy.archipelagoTip, hanging: copy.hangingTip }[next.level.type];
  if (next.level.type === "tower") {
    const goal = next.level.goalValue.toFixed(0);
    heightElement.textContent = `${formatNumber(next.height, 1)} / ${goal} m`;
    goalCopyElement.textContent = `${copy.goalTower} ${goal} m`;
    focusTopButton.textContent = copy.focusTower;
  } else if (next.level.goals?.length) {
    const count = next.level.goals.filter((goal) => goal.reached).length;
    heightElement.textContent = `${count} / ${next.level.goals.length} ${copy.targets}`;
    goalCopyElement.textContent = next.level.type === "hanging" ? `${copy.buildDown} · ${formatNumber(next.level.goalValue, 0)} m`
      : next.level.type === "archipelago" ? copy.connectAll : `${copy.chasm}: ${formatNumber(next.level.goalValue, 1)} m`;
    focusTopButton.textContent = next.level.type === "hanging" ? copy.focusDown : copy.focusBridge;
  } else {
    const leftEdge = next.level.grounds[0].x + next.level.grounds[0].width / 2;
    const frontier = Math.max(leftEdge, ...[...bodies.values()].filter((body) => !body.anchor || body.anchorGroup === "start").map((body) => body.x));
    const progress = Math.max(0, Math.min(next.level.goalValue, frontier - leftEdge));
    heightElement.textContent = `${formatNumber(progress, 1)} / ${formatNumber(next.level.goalValue, 1)} m`;
    goalCopyElement.textContent = `${copy.chasm}: ${formatNumber(next.level.goalValue, 1)} m`;
    focusTopButton.textContent = copy.focusBridge;
    tipElement.textContent = copy.bridgeTip;
  }
}

function updateVictory(completedBy: string | null) {
  const copy = COPY[language];
  victoryTitle.textContent = completedBy ? copy.victoryBy(completedBy) : copy.victoryReached;
}

function setConnection(state: "connecting" | "online" | "offline") {
  connectionState = state;
  connectionElement.className = `server-pill ${state}`;
  connectionElement.querySelector("b")!.textContent = COPY[language].connection[state];
}

function loadNickname() {
  const stored = window.localStorage.getItem("goo-tower-name");
  if (stored) return stored;
  const choices = COPY[language];
  const first = choices.nickFirst[Math.floor(Math.random() * choices.nickFirst.length)];
  const second = choices.nickSecond[Math.floor(Math.random() * choices.nickSecond.length)];
  const generated = `${first} ${second} ${Math.floor(10 + Math.random() * 90)}`;
  window.localStorage.setItem("goo-tower-name", generated);
  return generated;
}

function showToast(message: string, warning = false) {
  window.clearTimeout(toastTimer);
  toast.textContent = message;
  toast.classList.toggle("warning", warning);
  toast.hidden = false;
  toastTimer = window.setTimeout(() => { toast.hidden = true; }, 2_700);
}

function resizeCanvas() {
  const rect = canvas.getBoundingClientRect();
  const ratio = Math.min(window.devicePixelRatio || 1, 2);
  viewportWidth = rect.width;
  viewportHeight = rect.height;
  canvas.width = Math.max(1, Math.floor(rect.width * ratio));
  canvas.height = Math.max(1, Math.floor(rect.height * ratio));
  context.setTransform(ratio, 0, 0, ratio, 0, 0);
}

function worldToScreen(point: Point): Point {
  return { x: viewportWidth / 2 + (point.x - camera.x) * scale, y: viewportHeight * 0.64 + (point.y - camera.y) * scale };
}

function screenToWorld(point: Point): Point {
  return { x: camera.x + (point.x - viewportWidth / 2) / scale, y: camera.y + (point.y - viewportHeight * 0.64) / scale };
}

function fitTower() {
  const values = [...bodies.values()].filter((body) => body.anchorGroup !== "support");
  if (values.length === 0) return;
  if (stats.level.type !== "tower") {
    const leftEdge = stats.level.grounds[0].x + stats.level.grounds[0].width / 2;
    scale = clamp(Math.min(58, viewportWidth / 18), 34, 58);
    const entry = stats.level.entry ?? { x: leftEdge - 1.6, y: -0.29 };
    camera.x = entry.x + (stats.level.type === "hanging" ? 0 : viewportWidth * 0.12 / scale);
    camera.y = entry.y + (stats.level.type === "hanging" ? viewportHeight * 0.18 / scale : -1.2);
    autoFollow = true;
    return;
  }
  const minY = Math.min(...values.map((body) => body.y));
  const minX = Math.min(...values.map((body) => body.x));
  const maxX = Math.max(...values.map((body) => body.x));
  const worldHeight = Math.max(5, Math.abs(minY) + 3);
  const worldWidth = Math.max(10, maxX - minX + 4);
  const fittedScale = Math.min(viewportHeight * 0.68 / worldHeight, viewportWidth * 0.78 / worldWidth);
  if (fittedScale < 20) { scale = 56; focusTop(); return; }
  scale = clamp(fittedScale, 20, 70);
  camera.x = (minX + maxX) / 2;
  camera.y = minY * 0.5 + 1.1;
}

function focusTop() {
  scale = Math.max(36, scale);
  const values = [...bodies.values()].filter((body) => body.anchorGroup !== "support");
  if (values.length === 0) return;
  if (stats.level.type !== "tower") {
    const component = connectedBodies("start");
    const frontierBodies = values.filter((body) => component.has(body.id));
    const targets = stats.level.goals?.filter((goal) => !goal.reached) ?? [];
    const distance = (body: RenderBody) => Math.min(...targets.map((goal) => Math.hypot(goal.x - body.x, goal.y - body.y)));
    const frontier = frontierBodies.reduce((best, body) => targets.length ? (distance(body) < distance(best) ? body : best) : (body.x > best.x ? body : best), frontierBodies[0]);
    if (frontier) {
      camera.x = frontier.x - viewportWidth * 0.18 / scale;
      camera.y = frontier.y + viewportHeight * (stats.level.type === "hanging" ? 0.18 : 0.15) / scale;
    }
    autoFollow = true;
    return;
  }
  const minY = Math.min(...values.map((body) => body.y));
  const topBodies = values.filter((body) => body.y < minY + 3);
  camera.x = topBodies.reduce((sum, body) => sum + body.x, 0) / topBodies.length;
  camera.y = minY + viewportHeight * 0.3 / scale;
  autoFollow = true;
}

function showOverview() {
  const points = [
    ...stats.level.grounds.flatMap((ground) => [{ x: ground.x - ground.width / 2, y: ground.y - ground.height / 2 }, { x: ground.x + ground.width / 2, y: ground.y + ground.height / 2 }]),
    ...(stats.level.supports ?? []), ...(stats.level.goals ?? []),
    ...[...bodies.values()].filter((body) => !body.anchor),
  ];
  if (stats.level.type === "tower") points.push({ x: stats.level.entry?.x ?? 0, y: stats.level.goalLine.value });
  const minX = Math.min(...points.map((point) => point.x));
  const maxX = Math.max(...points.map((point) => point.x));
  const minY = Math.min(...points.map((point) => point.y));
  const maxY = Math.max(...points.map((point) => point.y));
  scale = clamp(Math.min(viewportWidth * 0.8 / (maxX - minX + 6), viewportHeight * 0.55 / (maxY - minY + 8)), 1.2, 58);
  camera.x = (minX + maxX) / 2;
  camera.y = (minY + maxY) / 2 + viewportHeight * 0.16 / scale;
  autoFollow = false;
}

function zoomAt(factor: number, screenPoint: Point = { x: viewportWidth / 2, y: viewportHeight / 2 }) {
  const before = screenToWorld(screenPoint);
  scale = clamp(scale * factor, 1.2, 115);
  camera.x = before.x - (screenPoint.x - viewportWidth / 2) / scale;
  camera.y = before.y - (screenPoint.y - viewportHeight * 0.64) / scale;
  autoFollow = false;
}

function render(frameAt: number) {
  const elapsedSeconds = clamp((frameAt - lastFrameAt) / 1_000, 0, 0.05);
  lastFrameAt = frameAt;
  if (!panning && !helpDialog.open && pointerScreen && (edgePan.x !== 0 || edgePan.y !== 0)) {
    const worldSpeed = EDGE_PAN_SPEED / scale;
    camera.x += edgePan.x * worldSpeed * elapsedSeconds;
    camera.y += edgePan.y * worldSpeed * elapsedSeconds;
    pointerWorld = screenToWorld(pointerScreen);
    autoFollow = false;
  }
  if (stats.level.completed && stats.level.nextLevelAt) {
    victory.hidden = false;
    victoryCountdown.textContent = String(Math.max(0, Math.ceil((stats.level.nextLevelAt - Date.now()) / 1_000)));
  }
  for (const body of bodies.values()) {
    const smoothing = body.anchor ? 1 : 0.24;
    body.drawX += (body.x - body.drawX) * smoothing;
    body.drawY += (body.y - body.drawY) * smoothing;
    body.drawAngle += shortestAngle(body.angle - body.drawAngle) * smoothing;
  }

  drawBackground();
  drawGoal();
  drawGround();
  drawSource();
  drawLinks();
  drawBodies();
  drawArrivals();
  drawCandidate();
  requestAnimationFrame(render);
}

function drawBackground() {
  drawScenery(context, viewportWidth, viewportHeight, camera, stats.level.seed ?? stats.level.round);

  const topWorld = screenToWorld({ x: 0, y: 0 }).y;
  const bottomWorld = screenToWorld({ x: 0, y: viewportHeight }).y;
  const firstLevel = Math.floor(topWorld / 5) * 5;
  context.lineWidth = 1;
  context.font = "700 10px ui-monospace, monospace";
  context.textAlign = "left";
  for (let level = firstLevel; level <= bottomWorld; level += 5) {
    const y = worldToScreen({ x: 0, y: level }).y;
    context.strokeStyle = level === 0 ? "rgba(24,34,49,.28)" : "rgba(24,34,49,.075)";
    context.beginPath(); context.moveTo(0, y); context.lineTo(viewportWidth, y); context.stroke();
    if (level !== 0) {
      context.fillStyle = "rgba(37,56,45,.52)";
      context.fillText(`${level > 0 ? "↓ " : ""}${Math.abs(level)} m`, 13, y - 7);
    }
  }
}

function drawGround() {
  for (const ground of stats.level.grounds) {
    const topLeft = worldToScreen({ x: ground.x - ground.width / 2, y: ground.y - ground.height / 2 });
    const bottomRight = worldToScreen({ x: ground.x + ground.width / 2, y: ground.y + ground.height / 2 });
    const width = bottomRight.x - topLeft.x;
    const height = Math.max(12, bottomRight.y - topLeft.y);
    context.fillStyle = "#6b5740";
    roundedRect(context, topLeft.x, topLeft.y, width, height, Math.min(12, scale * 0.16));
    context.fill();
    context.fillStyle = "#738345";
    context.fillRect(topLeft.x, topLeft.y, width, Math.max(4, scale * 0.09));
    context.strokeStyle = "rgba(247,243,222,.11)";
    context.lineWidth = 1;
    for (let x = topLeft.x + 17; x < bottomRight.x; x += 28) {
      context.beginPath(); context.moveTo(x, topLeft.y + 14); context.lineTo(x - 9, topLeft.y + height); context.stroke();
    }
  }
}

function drawGoal() {
  const goal = stats.level.goalLine;
  const copy = COPY[language];
  if (stats.level.type !== "tower" && stats.level.goals?.length) {
    for (const [index, goal] of stats.level.goals.entries()) {
      const point = worldToScreen(goal);
      context.save();
      context.fillStyle = goal.reached ? "rgba(78,116,61,.13)" : "rgba(221,63,55,.13)";
      context.strokeStyle = goal.reached ? "rgba(56,97,41,.7)" : "rgba(221,63,55,.5)";
      context.lineWidth = 4;
      context.setLineDash(goal.reached ? [] : [8, 7]);
      context.beginPath(); context.arc(point.x, point.y, Math.max(17, scale * 1.2), 0, Math.PI * 2); context.fill(); context.stroke();
      context.setLineDash([]);
      context.font = "900 11px ui-monospace, monospace"; context.textAlign = "center";
      context.strokeStyle = "#f3edda"; context.lineWidth = 4;
      const label = `${goal.reached ? "✓" : copy.target} ${index + 1}`;
      context.strokeText(label, point.x, point.y - Math.max(25, scale * 1.45));
      context.fillStyle = goal.reached ? "#36552d" : "#9d3c31";
      context.fillText(label, point.x, point.y - Math.max(25, scale * 1.45));
      context.restore();
    }
    return;
  }
  context.save();
  context.strokeStyle = "rgba(221, 63, 55, .5)";
  context.fillStyle = "rgba(176, 40, 35, .78)";
  context.lineWidth = 8;
  context.setLineDash([18, 12]);
  context.font = "900 11px ui-monospace, monospace";
  context.letterSpacing = "1px";
  if (goal.axis === "y") {
    const y = worldToScreen({ x: 0, y: goal.value }).y;
    context.beginPath(); context.moveTo(0, y); context.lineTo(viewportWidth, y); context.stroke();
    context.setLineDash([]);
    context.fillText(`${copy.winLine} · ${stats.level.goalValue.toFixed(0)} M`, 18, y - 13);
  } else {
    const x = worldToScreen({ x: goal.value, y: 0 }).x;
    context.beginPath(); context.moveTo(x, 0); context.lineTo(x, viewportHeight); context.stroke();
    context.setLineDash([]);
    context.save(); context.translate(x - 13, 18); context.rotate(Math.PI / 2); context.fillText(copy.otherSide, 0, 0); context.restore();
  }
  context.restore();
}

function drawSource() {
  const point = worldToScreen(stats.level.source);
  if (point.x < -80 || point.x > viewportWidth + 80 || point.y < -80 || point.y > viewportHeight + 80) return;
  const pulse = 1 + Math.sin(performance.now() / 420) * 0.08;
  context.save();
  context.translate(point.x, point.y);
  context.scale(pulse, pulse);
  context.fillStyle = "rgba(190,219,84,.2)";
  context.beginPath(); context.arc(0, 0, 27, 0, Math.PI * 2); context.fill();
  context.fillStyle = "#bedb54";
  context.beginPath(); context.arc(0, 0, 15, 0, Math.PI * 2); context.fill();
  context.strokeStyle = "#253447"; context.lineWidth = 2; context.stroke();
  context.fillStyle = "#253447"; context.font = "900 8px ui-monospace, monospace"; context.textAlign = "center"; context.fillText("GOO", 0, 3);
  context.restore();
}

function drawLinks() {
  context.lineCap = "round";
  for (const link of links.values()) {
    const a = bodies.get(link.a);
    const b = bodies.get(link.b);
    if (!a || !b) continue;
    const start = worldToScreen({ x: a.drawX, y: a.drawY });
    const end = worldToScreen({ x: b.drawX, y: b.drawY });
    context.beginPath(); context.moveTo(start.x, start.y); context.lineTo(end.x, end.y);
    context.strokeStyle = "#253447"; context.lineWidth = Math.max(7, scale * 0.19); context.stroke();
    context.strokeStyle = stressEnabled ? stressColor(linkStress.get(link.id) ?? 0) : "#91aa3e";
    context.lineWidth = Math.max(3, scale * 0.085); context.stroke();
  }
}

function stressColor(force: number): string {
  const neutral = [169, 176, 168];
  const extreme = force < 0 ? [29, 91, 221] : [220, 50, 48];
  const amount = Math.sqrt(clamp(Math.abs(force) / LINK_TENSION_BREAK_FORCE, 0, 1));
  return `rgb(${neutral.map((channel, index) => Math.round(channel + (extreme[index] - channel) * amount)).join(", ")})`;
}

function drawBodies() {
  const time = performance.now() / 900;
  for (const body of bodies.values()) {
    if (arrivals.has(body.id)) continue;
    const point = worldToScreen({ x: body.drawX, y: body.drawY });
    const radius = Math.max(4, scale * (body.anchor ? 0.28 : 0.22));
    if (point.x < -radius || point.x > viewportWidth + radius || point.y < -radius || point.y > viewportHeight + radius) continue;
    context.save();
    context.translate(point.x, point.y);
    context.rotate(body.drawAngle);
    context.shadowColor = "rgba(255,249,216,.9)";
    context.shadowBlur = 11;
    context.shadowOffsetY = 0;
    context.beginPath(); context.arc(0, 0, radius, 0, Math.PI * 2);
    context.fillStyle = body.color; context.fill();
    context.shadowColor = "transparent";
    context.strokeStyle = "#172332"; context.lineWidth = Math.max(2, scale * 0.045); context.stroke();
    if (body.anchorGroup === "support") {
      context.fillStyle = "#172332";
      context.beginPath(); context.arc(0, 0, Math.max(1, radius * 0.18), 0, Math.PI * 2); context.fill();
      context.restore();
      continue;
    }
    if (radius < 8) { context.restore(); continue; }
    const eyeOffset = radius * 0.31;
    const eyeY = -radius * 0.13;
    const eyeRadius = Math.max(3.5, radius * 0.18);
    context.fillStyle = "#fffdf1";
    context.beginPath(); context.arc(-eyeOffset, eyeY, eyeRadius, 0, Math.PI * 2); context.arc(eyeOffset, eyeY, eyeRadius, 0, Math.PI * 2); context.fill();
    const glance = Math.sin(time + body.id) * eyeRadius * 0.24;
    context.fillStyle = "#172332";
    context.beginPath(); context.arc(-eyeOffset + glance, eyeY + 1, eyeRadius * 0.42, 0, Math.PI * 2); context.arc(eyeOffset + glance, eyeY + 1, eyeRadius * 0.42, 0, Math.PI * 2); context.fill();
    context.restore();
  }
}

function drawArrivals() {
  const now = performance.now();
  for (const [bodyId, arrival] of arrivals) {
    const progress = clamp((now - arrival.startedAt) / 520, 0, 1);
    const eased = 1 - (1 - progress) ** 3;
    const arc = Math.sin(progress * Math.PI) * -1.2;
    const worldPoint = {
      x: arrival.from.x + (arrival.to.x - arrival.from.x) * eased,
      y: arrival.from.y + (arrival.to.y - arrival.from.y) * eased + arc,
    };
    const point = worldToScreen(worldPoint);
    const radius = Math.max(5, scale * 0.22);
    context.beginPath(); context.arc(point.x, point.y, radius, 0, Math.PI * 2);
    context.fillStyle = bodies.get(bodyId)?.color ?? "#bedb54"; context.fill();
    context.strokeStyle = "#172332"; context.lineWidth = 2; context.stroke();
    if (progress >= 1) arrivals.delete(bodyId);
  }
}

function drawCandidate() {
  if (mode !== "build" || !pointerWorld || !pointerScreen || loading.hidden === false) return;
  const candidate = inspectCandidate(pointerWorld);
  const position = worldToScreen(pointerWorld);
  for (const neighbor of candidate.neighbors) {
    const target = worldToScreen({ x: neighbor.drawX, y: neighbor.drawY });
    context.beginPath(); context.moveTo(position.x, position.y); context.lineTo(target.x, target.y);
    context.setLineDash([6, 6]);
    context.strokeStyle = candidate.valid ? "rgba(78,104,34,.7)" : "rgba(193,71,54,.65)";
    context.lineWidth = 4; context.stroke();
    context.setLineDash([]);
  }
  context.globalAlpha = 0.72;
  context.beginPath(); context.arc(position.x, position.y, Math.max(5, scale * 0.22), 0, Math.PI * 2);
  context.fillStyle = candidate.valid ? "#bedb54" : "#ef7156"; context.fill();
  context.strokeStyle = "#172332"; context.lineWidth = 2; context.stroke();
  context.globalAlpha = 1;
}

canvas.addEventListener("pointermove", (event) => {
  const rect = canvas.getBoundingClientRect();
  pointerScreen = { x: event.clientX - rect.left, y: event.clientY - rect.top };
  pointerWorld = screenToWorld(pointerScreen);
  updateEdgePan(pointerScreen, event.pointerType);
  if (panning && pointerDown) {
    const dx = pointerScreen.x - pointerDown.x;
    const dy = pointerScreen.y - pointerDown.y;
    camera.x -= dx / scale;
    camera.y -= dy / scale;
    pointerDown = pointerScreen;
    autoFollow = false;
  }
  updateHoverCard();
});

canvas.addEventListener("pointerdown", (event) => {
  canvas.setPointerCapture(event.pointerId);
  const rect = canvas.getBoundingClientRect();
  pointerDown = { x: event.clientX - rect.left, y: event.clientY - rect.top };
  edgePan.x = 0;
  edgePan.y = 0;
  if (mode === "pan" || event.button === 1 || event.shiftKey) {
    panning = true;
    canvas.classList.add("panning");
  }
});

canvas.addEventListener("pointerup", (event) => {
  const rect = canvas.getBoundingClientRect();
  const up = { x: event.clientX - rect.left, y: event.clientY - rect.top };
  const moved = pointerDown ? Math.hypot(up.x - pointerDown.x, up.y - pointerDown.y) : 0;
  if (mode === "build" && !panning && moved < 8) void placeAt(screenToWorld(up));
  panning = false;
  pointerDown = null;
  canvas.classList.remove("panning");
  updateEdgePan(up, event.pointerType);
});

canvas.addEventListener("pointercancel", () => {
  panning = false;
  pointerDown = null;
  edgePan.x = 0;
  edgePan.y = 0;
  canvas.classList.remove("panning");
});

canvas.addEventListener("pointerleave", () => {
  edgePan.x = 0;
  edgePan.y = 0;
  if (!panning) {
    pointerWorld = null;
    pointerScreen = null;
    hoverCard.hidden = true;
  }
});

function updateEdgePan(point: Point, pointerType: string) {
  if (pointerType !== "mouse" || panning) {
    edgePan.x = 0;
    edgePan.y = 0;
    return;
  }
  edgePan.x = edgePanAxis(point.x, viewportWidth);
  edgePan.y = edgePanAxis(point.y, viewportHeight);
}

function edgePanAxis(position: number, size: number): number {
  if (position < EDGE_PAN_MARGIN) return -(1 - clamp(position / EDGE_PAN_MARGIN, 0, 1));
  if (position > size - EDGE_PAN_MARGIN) return 1 - clamp((size - position) / EDGE_PAN_MARGIN, 0, 1);
  return 0;
}

canvas.addEventListener("wheel", (event) => {
  event.preventDefault();
  const rect = canvas.getBoundingClientRect();
  zoomAt(event.deltaY < 0 ? 1.1 : 0.9, { x: event.clientX - rect.left, y: event.clientY - rect.top });
}, { passive: false });

document.querySelectorAll<HTMLButtonElement>("[data-mode]").forEach((button) => {
  button.addEventListener("click", () => {
    mode = button.dataset.mode as "build" | "pan";
    document.querySelectorAll("[data-mode]").forEach((item) => item.classList.toggle("active", item === button));
    canvas.classList.toggle("pan-mode", mode === "pan");
    hoverCard.hidden = true;
  });
});

stressButton.addEventListener("click", () => {
  stressEnabled = !stressEnabled;
  stressButton.classList.toggle("active", stressEnabled);
  stressButton.setAttribute("aria-pressed", String(stressEnabled));
  stressLegend.hidden = !stressEnabled;
  tipElement.hidden = stressEnabled;
  if (!stressEnabled) linkStress.clear();
  if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: "set_stress", enabled: stressEnabled }));
});

document.querySelector("#zoom-in")!.addEventListener("click", () => zoomAt(1.18));
document.querySelector("#zoom-out")!.addEventListener("click", () => zoomAt(0.84));
document.querySelector("#focus-top")!.addEventListener("click", focusTop);
document.querySelector("#overview")!.addEventListener("click", showOverview);
resetVoteButton.addEventListener("click", () => {
  if (!socket || socket.readyState !== WebSocket.OPEN) {
    showToast(failureMessage("server_starting"), true);
    return;
  }
  socket.send(JSON.stringify({ type: "vote_reset" }));
});
document.querySelector("#help")!.addEventListener("click", () => helpDialog.showModal());
document.querySelector("#close-help")!.addEventListener("click", () => helpDialog.close());
helpDialog.addEventListener("click", (event) => { if (event.target === helpDialog) helpDialog.close(); });
document.querySelectorAll<HTMLButtonElement>("[data-language]").forEach((button) => {
  button.addEventListener("click", () => applyLanguage(button.dataset.language as Language));
});
window.addEventListener("storage", (event) => {
  if (event.key === LANGUAGE_KEY && (event.newValue === "sl" || event.newValue === "en")) applyLanguage(event.newValue);
});

function updateHoverCard() {
  if (mode !== "pan" || !pointerScreen || !pointerWorld) { hoverCard.hidden = true; return; }
  const nearest = [...bodies.values()]
    .map((body) => ({ body, distance: Math.hypot(pointerWorld!.x - body.x, pointerWorld!.y - body.y) }))
    .sort((a, b) => a.distance - b.distance)[0];
  if (!nearest || nearest.distance > 0.72) { hoverCard.hidden = true; return; }
  const copy = COPY[language];
  hoverCard.textContent = nearest.body.anchorGroup === "support" ? copy.support : nearest.body.anchor ? copy.foundation : `${copy.addedBy}: ${nearest.body.owner}`;
  hoverCard.style.left = `${pointerScreen.x + 14}px`;
  hoverCard.style.top = `${pointerScreen.y + 14}px`;
  hoverCard.hidden = false;
}

function registerWebMcp() {
  const modelContext = (document as Document & { modelContext?: ModelContext }).modelContext;
  if (!modelContext?.registerTool) return;
  const lifecycle = new AbortController();
  void Promise.resolve(modelContext.registerTool({
    name: "get_tower_status",
    title: COPY[language].webStatusTitle,
    description: COPY[language].webStatusDescription,
    inputSchema: { type: "object", properties: {}, additionalProperties: false },
    annotations: { readOnlyHint: true, untrustedContentHint: false },
    execute: () => ({ ...stats }),
  }, { signal: lifecycle.signal })).catch(() => undefined);
  void Promise.resolve(modelContext.registerTool({
    name: "place_goo",
    title: COPY[language].webPlaceTitle,
    description: COPY[language].webPlaceDescription,
    inputSchema: {
      type: "object",
      properties: { x: { type: "number", minimum: WORLD_BOUNDS.minX, maximum: WORLD_BOUNDS.maxX }, y: { type: "number", minimum: WORLD_BOUNDS.minY, maximum: WORLD_BOUNDS.maxY } },
      required: ["x", "y"],
      additionalProperties: false,
    },
    annotations: { readOnlyHint: false, untrustedContentHint: false },
    async execute(input) {
      const value = input as { x?: unknown; y?: unknown };
      if (typeof value.x !== "number" || typeof value.y !== "number") throw new Error(COPY[language].coordinateError);
      const result = await requestPlacement(value.x, value.y);
      if (!result.ok) throw new Error(failureMessage(result.reason));
      return { ok: true, bodyId: result.bodyId, bodyCount: stats.bodyCount };
    },
  }, { signal: lifecycle.signal })).catch(() => undefined);
}

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
  const r = Math.min(radius, Math.abs(width) / 2, Math.abs(height) / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}

function shortestAngle(value: number) {
  return Math.atan2(Math.sin(value), Math.cos(value));
}
function readLanguage(): Language {
  const stored = window.localStorage.getItem(LANGUAGE_KEY);
  return stored === "sl" || stored === "en" ? stored : "en";
}
function applyLanguage(next: Language) {
  language = next;
  const copy = COPY[language];
  window.localStorage.setItem(LANGUAGE_KEY, language);
  document.documentElement.lang = language;
  document.title = `${copy.title} · games.ddsq.si`;
  document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute(
    "content",
    language === "sl"
      ? "Skupni živi stolp, ki ga skupaj gradijo vsi obiskovalci."
      : "A shared living tower built together by every visitor.",
  );
  setText("#brand-title", copy.title);
  setText("#brand-subtitle", copy.subtitle);
  setText("#help", copy.help);
  setText("#goo-count-label", copy.gooCount);
  setText("#builders-label", copy.builders);
  setText("#moving-label", copy.moving);
  setText("#build-label", copy.build);
  setText("#pan-label", copy.pan);
  setText("#stress-label", copy.stress);
  setText("#overview", `⊞ ${copy.overview}`);
  setText("#tension-label", copy.tension);
  setText("#compression-label", copy.compression);
  setText("#loading-title", copy.loadingTitle);
  setText("#loading-body", copy.loadingBody);
  setText("#victory-eyebrow", copy.victoryEyebrow);
  setText("#next-map-label", copy.nextMap);
  setText("#seconds-label", copy.seconds);
  setText("#help-eyebrow", copy.help);
  setText("#help-title", copy.helpTitle);
  setText("#help-build-label", `${copy.build}:`);
  setText("#help-build-copy", copy.helpBuild);
  setText("#help-pan-label", `${copy.pan}:`);
  setText("#help-pan-copy", copy.helpPan);
  setText("#help-stress-label", `${copy.stress}:`);
  setText("#help-stress-copy", copy.helpStress);
  setText("#help-server-copy", copy.helpServer);
  setText("#help-note", copy.helpNote);
  document.querySelector("#brand")?.setAttribute("aria-label", copy.back);
  document.querySelector("#stage")?.setAttribute("aria-label", copy.stageAria);
  canvas.setAttribute("aria-label", copy.canvasAria);
  document.querySelector("#stats")?.setAttribute("aria-label", copy.statsAria);
  document.querySelector("#tools")?.setAttribute("aria-label", copy.toolsAria);
  document.querySelector("#zoom-out")?.setAttribute("aria-label", copy.zoomOut);
  document.querySelector("#zoom-in")?.setAttribute("aria-label", copy.zoomIn);
  document.querySelector("#close-help")?.setAttribute("aria-label", copy.close);
  document.querySelector("#language-switch")?.setAttribute("aria-label", copy.languageLabel);
  document.querySelectorAll<HTMLButtonElement>("[data-language]").forEach((button) => {
    button.setAttribute("aria-pressed", String(button.dataset.language === language));
  });
  setConnection(connectionState);
  updateStats(stats);
  updateVictory(stats.level.completedBy);
  updateResetVoteButton();
  hoverCard.hidden = true;
}
function updateResetVoteButton() {
  const copy = COPY[language];
  resetVoteButton.textContent = `↻ ${copy.reset} ${resetVote.votes}/${resetVote.required}`;
  resetVoteButton.classList.toggle("voted", resetVote.voted);
  resetVoteButton.setAttribute("aria-pressed", String(resetVote.voted));
  resetVoteButton.setAttribute("aria-label", resetVote.voted ? copy.resetRemoveAria : copy.resetVoteAria);
}
function failureMessage(reason: PlacementFailure): string { return COPY[language].failures[reason]; }
function localizeNotice(message: string): string {
  if (language === "sl") return message;
  if (message === COPY.sl.noticeUnavailable) return COPY.en.noticeUnavailable;
  if (message === COPY.sl.noticeStarting) return COPY.en.noticeStarting;
  return message;
}
function formatNumber(value: number, digits: number): string {
  return new Intl.NumberFormat(language === "sl" ? "sl-SI" : "en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}
function setText(selector: string, value: string) { document.querySelector(selector)!.textContent = value; }
function clamp(value: number, min: number, max: number) { return Math.min(max, Math.max(min, value)); }
function round(value: number, places: number) { const factor = 10 ** places; return Math.round(value * factor) / factor; }

new ResizeObserver(resizeCanvas).observe(canvas);
resizeCanvas();
requestAnimationFrame(render);
